import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '../store/gameStore';
import { gameAPI, playerAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';
import i18n from '../i18n/config';

// The invite-link entry point. A visitor here is always an INVITED PLAYER (never the host/admin).
// What we show depends on whether the host has generated the world yet:
//  - not generated (status 'setup') → a lobby: join the roster, then wait for the host to start.
//  - generated ('active'/'paused')  → join as a player and go straight into the game.
export default function JoinGame() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { setCurrentGame, setPlayers } = useGameStore();

  const [game, setGame] = useState<any>(null);
  const [status, setStatus] = useState<'loading' | 'form' | 'waiting' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const playerIdRef = useRef<string | null>(null);

  const userData = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || 'null'); } catch { return null; }
  })();
  const userId: string | null = userData?.user?.id || null;

  const notGenerated = (g: any) => g?.status === 'setup';
  const enterGame = (gameId: string, pid: string | null) => {
    window.location.href = pid ? `/game/${gameId}?playerId=${pid}` : `/game/${gameId}`;
  };

  // Fetch the game the link points to.
  useEffect(() => {
    (async () => {
      const parts = window.location.pathname.split('/');
      const shareCode = parts[parts.length - 1];
      if (!shareCode) { setError(t('join.invalidLink')); setStatus('error'); return; }

      try {
        const g = await gameAPI.joinGameByShareCode(shareCode);
        setGame(g);
        setCurrentGame(g);
        setPlayers(g.players || []);

        // Follow the admin's language: show the invitee the interface in the game's language
        // (the creator chose it), if we have a translation for it — otherwise English.
        if (g.language && i18n.hasResourceBundle(g.language, 'translation')) {
          i18n.changeLanguage(g.language);
        }

        // Already joined this game on this device before? Skip the name step.
        const existing = localStorage.getItem(`game_${g.id}_player`);
        if (existing) {
          playerIdRef.current = existing;
          if (notGenerated(g)) setStatus('waiting');
          else enterGame(g.id, existing);
          return;
        }

        // Prefill with the account display name if we have one.
        const dn = userData?.user?.displayName || userData?.user?.display_name || userData?.user?.name;
        if (dn) setName(dn);
        setStatus('form');
      } catch (e: any) {
        setError(e.response?.data?.error || t('join.notFound'));
        setStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lobby: poll for the host starting the world, then drop the player into the game.
  useEffect(() => {
    if (status !== 'waiting' || !game?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await gameAPI.getGame(game.id);
        if (cancelled) return;
        if (!notGenerated(fresh)) enterGame(game.id, playerIdRef.current);
      } catch { /* keep waiting */ }
    };
    const iv = setInterval(tick, 4000);
    tick();
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, game?.id]);

  const handleJoin = async () => {
    if (!name.trim() || !game) return;
    setJoining(true);
    setError(null);
    try {
      const player = await playerAPI.addPlayer(game.id, name.trim());
      playerIdRef.current = player.id;
      localStorage.setItem(`game_${game.id}_player`, player.id);
      // Record this as a game the user was invited to (safe: server links as 'player').
      if (userId) gameAPI.claimGame(game.id, userId, 'player').catch(() => {});

      if (notGenerated(game)) setStatus('waiting');
      else enterGame(game.id, player.id);
    } catch (e: any) {
      setError(e.response?.data?.error || t('join.joinFailed'));
    } finally {
      setJoining(false);
    }
  };

  const card: React.CSSProperties = {
    maxWidth: '500px', width: '100%',
    background: 'rgba(15, 12, 41, 0.8)', backdropFilter: 'blur(20px)',
    borderRadius: '24px', border: '1px solid rgba(99, 102, 241, 0.3)',
    padding: isMobile ? '1.75rem 1.25rem' : '3rem 2rem', textAlign: 'center',
  };
  const h2: React.CSSProperties = {
    fontSize: isMobile ? '1.4rem' : '2rem', marginBottom: '0.75rem', color: '#e2e8f0', lineHeight: 1.25,
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: isMobile ? '1rem' : '2rem',
    }}>
      <div style={card}>
        {status === 'loading' && (
          <>
            <div style={{ fontSize: '3.5rem', marginBottom: '1.25rem' }}>🔄</div>
            <h2 style={h2}>{t('join.loadingTitle')}</h2>
            <p style={{ color: '#a0aec0' }}>{t('join.loadingSubtitle')}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ fontSize: '3.5rem', marginBottom: '1.25rem' }}>❌</div>
            <h2 style={{ ...h2, color: '#ef4444' }}>{t('common.error')}</h2>
            <p style={{ color: '#fca5a5', marginBottom: '1.75rem' }}>{error}</p>
            <button onClick={() => (window.location.href = '/')} style={primaryBtn}>
              {t('nav.home')}
            </button>
          </>
        )}

        {status === 'form' && game && (
          <>
            <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🎲</div>
            <h2 style={h2}>{game.name}</h2>
            <p style={{ color: '#a0aec0', marginBottom: '0.35rem' }}>🌍 {game.universe}</p>
            <p style={{ color: '#a0aec0', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              {notGenerated(game)
                ? t('join.lobbyNote')
                : t('join.joinAsPlayerNote')}
            </p>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('join.namePlaceholder')}
              onKeyDown={(e) => { if (e.key === 'Enter') handleJoin(); }}
              style={{
                width: '100%', padding: '0.9rem 1rem', fontSize: '1rem', marginBottom: '1rem',
                border: '2px solid rgba(99, 102, 241, 0.3)', borderRadius: '10px',
                background: 'rgba(0,0,0,0.3)', color: '#fff', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <button onClick={handleJoin} disabled={joining || !name.trim()} style={{
              ...primaryBtn, width: '100%',
              opacity: joining || !name.trim() ? 0.5 : 1,
              cursor: joining || !name.trim() ? 'not-allowed' : 'pointer',
            }}>
              {joining ? `⏳ ${t('join.connecting')}` : (notGenerated(game) ? `✅ ${t('join.joinAsPlayer')}` : `🎮 ${t('join.enterGame')}`)}
            </button>
            {error && <p style={{ color: '#fca5a5', marginTop: '1rem' }}>{error}</p>}
          </>
        )}

        {status === 'waiting' && game && (
          <>
            <div style={{ fontSize: '3.5rem', marginBottom: '1.25rem', animation: 'pulse 2s infinite' }}>⏳</div>
            <h2 style={h2}>{t('join.waitingTitle')}{name ? `, ${name}` : ''}!</h2>
            <p style={{ color: '#a0aec0', marginBottom: '0.75rem' }}>
              {t('join.waitingHostNote', { name: game.name })}
            </p>
            <p style={{ color: '#718096', fontSize: '0.85rem' }}>
              {t('join.waitingAuto')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  padding: '0.95rem 2rem', fontSize: '1rem', fontWeight: 'bold', border: 'none',
  borderRadius: '12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff', cursor: 'pointer',
};
